import { DateTime } from 'luxon';
import { HOUR_MS } from '../../../../shared/charts/chart-utils';

const COMPASS_16 = [
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
] as const;

const TIMESTAMP_TOLERANCE_MS = 1000;

export interface HourlyGraphPoint {
  x: string;
  y: number;
  direction?: number;
  directionText?: string;
  generated?: boolean;
}

export const WEATHER_GRAPH_TYPES_WITH_HOURLY_GAP_FILL = [
  'wind',
  'swell-height',
  'swell-period',
] as const;

export type WeatherGraphTypeWithHourlyGapFill =
  (typeof WEATHER_GRAPH_TYPES_WITH_HOURLY_GAP_FILL)[number];

const normalizeDegrees = (degrees: number): number =>
  ((degrees % 360) + 360) % 360;

export const interpolateLinear = (
  start: number,
  end: number,
  ratio: number,
): number => start + ratio * (end - start);

export const interpolateAngleDegrees = (
  start: number,
  end: number,
  ratio: number,
): number => {
  const startDegrees = normalizeDegrees(start);
  const endDegrees = normalizeDegrees(end);
  let delta = endDegrees - startDegrees;

  if (delta > 180) {
    delta -= 360;
  } else if (delta < -180) {
    delta += 360;
  }

  return normalizeDegrees(startDegrees + delta * ratio);
};

export const directionDegreesToCompassText = (degrees: number): string => {
  const normalized = normalizeDegrees(degrees);
  const index = Math.round(normalized / 22.5) % 16;
  return COMPASS_16[index];
};

export const normalizeGraphPointX = (x: unknown): string | null => {
  if (typeof x === 'string' && x.length > 0) {
    return x;
  }

  if (typeof x === 'number' && Number.isFinite(x)) {
    const millis = x < 1e12 ? x * 1000 : x;
    return new Date(millis).toISOString();
  }

  return null;
};

export const resolveGraphPointTimeZone = (
  timeZone?: string,
): string | undefined => {
  if (!timeZone) {
    return undefined;
  }

  const zoneCheck = DateTime.now().setZone(timeZone);
  return zoneCheck.isValid ? timeZone : undefined;
};

const coerceGraphPoints = <T extends HourlyGraphPoint>(
  points: unknown,
): T[] => {
  if (!Array.isArray(points)) {
    return [];
  }

  return points
    .map((point) => {
      if (point == null || typeof point !== 'object') {
        return null;
      }

      const normalizedX = normalizeGraphPointX((point as HourlyGraphPoint).x);
      if (!normalizedX) {
        return null;
      }

      return {
        ...(point as T),
        x: normalizedX,
      };
    })
    .filter((point): point is T => point != null);
};

const hourStartMillis = (isoTime: string, timeZone?: string): number | null => {
  const parsed = timeZone
    ? DateTime.fromISO(isoTime, { zone: timeZone })
    : DateTime.fromISO(isoTime);

  if (!parsed.isValid) {
    return null;
  }

  return parsed.startOf('hour').toMillis();
};

const nextHourStartMillis = (hourMillis: number, timeZone?: string): number =>
  timeZone
    ? DateTime.fromMillis(hourMillis, { zone: timeZone })
        .plus({ hours: 1 })
        .startOf('hour')
        .toMillis()
    : hourMillis + HOUR_MS;

const toGraphPointIso = (millis: number, anchorIso: string): string => {
  const target = DateTime.fromMillis(millis);

  if (!target.isValid) {
    return anchorIso;
  }

  // Match API output: UTC `Z` timestamps (see interface-weather date-helpers).
  return target.toUTC().toISO() ?? anchorIso;
};

const interpolateGraphPoint = <T extends HourlyGraphPoint>(
  start: T,
  end: T,
  targetMillis: number,
): T => {
  const startMillis = DateTime.fromISO(start.x).toMillis();
  const endMillis = DateTime.fromISO(end.x).toMillis();
  const span = endMillis - startMillis || 1;
  const ratio = (targetMillis - startMillis) / span;

  const startValue = Number(start.y);
  const endValue = Number(end.y);
  const interpolated: HourlyGraphPoint = {
    x: toGraphPointIso(targetMillis, start.x),
    y: interpolateLinear(startValue, endValue, ratio),
    generated: true,
  };

  if (start.direction != null && end.direction != null) {
    interpolated.direction = interpolateAngleDegrees(
      start.direction,
      end.direction,
      ratio,
    );
    interpolated.directionText = directionDegreesToCompassText(
      interpolated.direction,
    );
  } else if (start.directionText && end.directionText) {
    interpolated.directionText =
      ratio < 0.5 ? start.directionText : end.directionText;
  }

  return {
    ...start,
    ...interpolated,
  };
};

const sortGraphPointsByTime = <T extends HourlyGraphPoint>(points: T[]): T[] =>
  [...points].sort(
    (left, right) =>
      DateTime.fromISO(left.x).toMillis() -
      DateTime.fromISO(right.x).toMillis(),
  );

/**
 * Fills missing hourly samples between consecutive graph points.
 *
 * Forecast timestamps are treated as valid times: a reading at 1pm applies at
 * 1pm, so a 1pm→4pm gap is filled at 2pm and 3pm (never 12pm or 4pm).
 *
 * Source readings are always preserved — only missing hours are inserted.
 */
export const fillHourlyGapsInGraphPoints = <T extends HourlyGraphPoint>(
  points: unknown,
  timeZone?: string,
): T[] => {
  const normalizedPoints = coerceGraphPoints<T>(points);
  const resolvedTimeZone = resolveGraphPointTimeZone(timeZone);

  if (normalizedPoints.length < 2) {
    return [...normalizedPoints];
  }

  const sortedPoints = sortGraphPointsByTime(normalizedPoints);

  const occupiedHours = new Set<number>();
  sortedPoints.forEach((point) => {
    const hourKey = hourStartMillis(point.x, resolvedTimeZone);
    if (hourKey != null) {
      occupiedHours.add(hourKey);
    }
  });

  const generatedPoints: T[] = [];

  for (let index = 0; index < sortedPoints.length - 1; index += 1) {
    const startPoint = sortedPoints[index];
    const endPoint = sortedPoints[index + 1];
    const startHour = hourStartMillis(startPoint.x, resolvedTimeZone);
    const endHour = hourStartMillis(endPoint.x, resolvedTimeZone);

    if (startHour == null || endHour == null) {
      continue;
    }

    let cursor = nextHourStartMillis(startHour, resolvedTimeZone);

    while (cursor < endHour - TIMESTAMP_TOLERANCE_MS) {
      if (!occupiedHours.has(cursor)) {
        generatedPoints.push(
          interpolateGraphPoint(startPoint, endPoint, cursor),
        );
        occupiedHours.add(cursor);
      }

      cursor = nextHourStartMillis(cursor, resolvedTimeZone);
    }
  }

  return sortGraphPointsByTime([...sortedPoints, ...generatedPoints]);
};

export const fillHourlyGapsForWeatherGraphType = <T extends HourlyGraphPoint>(
  points: unknown,
  graphType: string,
  timeZone?: string,
): T[] =>
  WEATHER_GRAPH_TYPES_WITH_HOURLY_GAP_FILL.includes(
    graphType as WeatherGraphTypeWithHourlyGapFill,
  )
    ? fillHourlyGapsInGraphPoints<T>(points, timeZone)
    : Array.isArray(points)
      ? [...(points as T[])]
      : [];

/** Never throws — returns the original points when gap-fill cannot run. */
export const safelyFillHourlyGapsForWeatherGraphType = <
  T extends HourlyGraphPoint,
>(
  points: unknown,
  graphType: string,
  timeZone?: string,
): T[] => {
  if (
    !WEATHER_GRAPH_TYPES_WITH_HOURLY_GAP_FILL.includes(
      graphType as WeatherGraphTypeWithHourlyGapFill,
    )
  ) {
    return Array.isArray(points) ? [...(points as T[])] : [];
  }

  if (!Array.isArray(points)) {
    return [];
  }

  const sourcePoints = [...(points as T[])];
  const normalizedPoints = coerceGraphPoints<T>(sourcePoints);

  // If coercion strips everything, keep the raw API payload rather than show no data.
  const pointsToFill =
    normalizedPoints.length > 0 ? normalizedPoints : sourcePoints;

  try {
    const filled = fillHourlyGapsInGraphPoints<T>(pointsToFill, timeZone);

    if (filled.length === 0 && pointsToFill.length > 0) {
      return pointsToFill;
    }

    return filled.length >= pointsToFill.length ? filled : pointsToFill;
  } catch {
    return pointsToFill;
  }
};
